import React, { ReactElement, useCallback } from 'react'
import createCase, { ICase, expandStateFilter } from 'lib/entities/Case'
import CaseRow from 'routes/Private/Cases/components/CaseRow'
import NoCases from 'routes/Private/NoCases'
import {
	getDefaultFilters,
	Filters,
	useCaseListView,
} from 'components/CaseListView'
import { RowRenderProps, ListRender } from '@copyrightagent/basic-components'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getCasesForList } from 'routes/Private/Cases/api'
import LoadingRow from 'routes/Private/Cases/components/CaseRow/LoadingRow'
import { parseQueryParams } from 'lib/utils'
import { toast } from 'store/utils'

interface IProps {
	onCasesLoad: (cases: ICase[]) => void
	onTotalCount: (count: number) => void
	onSetFilters: (filters: Filters) => void
}

export default function CaseList({
	onCasesLoad,
	onTotalCount,
	onSetFilters,
}: IProps): ReactElement | null {
	const location = useLocation()
	const { t } = useTranslation()
	const { triggerRefetch } = useCaseListView()

	function rowRenderer({ key, isVisible, style, item }: RowRenderProps<ICase>) {
		if (!item) return <LoadingRow key={key} style={style} />
		return (
			<CaseRow theCase={item} key={key} style={style} isVisible={isVisible} />
		)
	}

	const getCases = useCallback(
		async (startIndex: number, stopIndex: number) => {
			const pageSize = stopIndex - startIndex + 1
			const filters = parseQueryParams(getDefaultFilters(), location.search)

			// Expand grouped states (e.g., selecting "Open" filters for all 3 open states)
			filters.caseState = expandStateFilter(filters.caseState)

			const response = await getCasesForList(startIndex, pageSize, {
				filters,
			})

			// The backend rejects invalid filters (e.g. a search term shorter than
			// the trigram-backed minimum) with a 400. Surface the message instead
			// of crashing on the missing `cases` array.
			if (!response.isSuccessful) {
				const errorMessage = (response.data as { error?: string })?.error
				toast(errorMessage || t('searchCases/searchFailed'), 'error')
				return {
					isSuccessful: false,
					items: [],
					count: 0,
					filters: { countries: [], caseTypes: [], caseState: [] },
				}
			}

			const {
				data: { cases, count, countries, caseTypes, caseState },
			} = response

			const createdCases = cases.map((caseItem) => createCase(caseItem))

			return {
				isSuccessful: true,
				items: createdCases,
				count,
				filters: { countries, caseTypes, caseState },
			}
		},
		[location.search, t],
	)

	const updateFilters = useCallback(
		async (filtersToUpdate: {
			countries: string[]
			caseTypes: string[]
			caseState: string[]
		}) => {
			const filters: Filters = getDefaultFilters()
			if (filtersToUpdate.countries?.length)
				filters.countries = filtersToUpdate.countries
			if (filtersToUpdate.caseTypes?.length)
				filters.caseTypes = filtersToUpdate.caseTypes
			if (filtersToUpdate.caseState?.length)
				filters.caseState = filtersToUpdate.caseState
			onSetFilters(filters)
		},
		[onSetFilters],
	)

	return (
		<ListRender<ICase>
			rowRender={rowRenderer}
			noItemsRender={() => <NoCases />}
			onUpdateTotalCount={onTotalCount}
			getItems={getCases}
			updateFilters={updateFilters}
			onItemsLoad={onCasesLoad}
			triggerRefetch={triggerRefetch}
			locationSearch={location.search}
		/>
	)
}
